import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Bot, MessageSquare, Users, CalendarDays, Save } from 'lucide-react';
import StatsCards from './StatsCards';
import ChatConversationDialog from './ChatConversationDialog';

const fmt = (v) => new Date(v).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const ChatManager = ({ onAuthError }) => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [convs, setConvs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([adminApi.get('/chat/config/admin'), adminApi.get('/chat/conversations')]);
      setCfg(c.data.data);
      setConvs(l.data);
    } catch (err) { onAuthError(err); }
  }, [onAuthError]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await adminApi.put('/chat/config', cfg);
      toast({ title: 'Guardado', description: data.message });
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: err.response?.data?.detail?.[0]?.msg || 'No se pudo guardar.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (sessionId) => {
    try {
      await adminApi.delete(`/chat/conversations/${sessionId}`);
      setSelected(null);
      toast({ title: 'Conversación eliminada' });
      load();
    } catch (err) { if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo eliminar.', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-8" data-testid="chat-manager">
      <StatsCards stats={convs} items={[
        { key: 'total_sessions', label: 'Conversaciones', icon: MessageSquare, color: 'from-blue-600 to-blue-400' },
        { key: 'last_7_days', label: 'Últimos 7 días', icon: CalendarDays, color: 'from-emerald-600 to-teal-400' },
        { key: 'total_messages', label: 'Mensajes', icon: Bot, color: 'from-amber-500 to-orange-400' },
        { key: 'leads', label: 'Leads desde el chat', icon: Users, color: 'from-red-600 to-rose-400' },
      ]} />

      {cfg && (
        <form onSubmit={save} className="bg-white/5 border border-white/10 rounded-3xl p-5 sm:p-6 space-y-5" data-testid="chat-config-form">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-red-500 flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
              <div>
                <h2 className="font-semibold">Asistente con IA</h2>
                <p className="text-xs text-slate-400">Responde con tus FAQ y las reglas del comparador (GPT-5.4 mini).</p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" data-testid="chat-enabled-toggle" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="h-5 w-5 accent-blue-500" />
              <span className={cfg.enabled ? 'text-emerald-300' : 'text-slate-400'}>{cfg.enabled ? 'Activo' : 'Desactivado'}</span>
            </label>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-slate-300">Mensaje de bienvenida</label>
            <Input data-testid="chat-welcome-input" value={cfg.welcome} onChange={(e) => setCfg({ ...cfg, welcome: e.target.value })} maxLength={300} className="h-11 bg-slate-950 border-white/10 text-white rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-slate-300">Instrucciones extra para el asistente (opcional)</label>
            <Textarea data-testid="chat-instructions-input" value={cfg.instrucciones} onChange={(e) => setCfg({ ...cfg, instrucciones: e.target.value })} rows={4} maxLength={2000} placeholder="Ej: Este mes promociona Runaway para estudiantes. No hables de seguros de auto." className="bg-slate-950 border-white/10 text-white rounded-xl" />
            <p className="text-xs text-slate-500 text-right">{cfg.instrucciones.length}/2000</p>
          </div>
          <Button type="submit" disabled={saving} data-testid="chat-config-save" className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 w-full sm:w-auto">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar configuración'}
          </Button>
        </form>
      )}

      <section className="bg-white/5 border border-white/10 rounded-3xl p-5 sm:p-6 space-y-4" data-testid="chat-conversations">
        <h2 className="font-semibold">Conversaciones recientes</h2>
        {convs?.data.length === 0 && <p className="text-sm text-slate-400" data-testid="chat-conversations-empty">Aún no hay conversaciones.</p>}
        <ul className="divide-y divide-white/5">
          {convs?.data.map((c) => (
            <li key={c.session_id}>
              <button type="button" onClick={() => setSelected(c.session_id)} data-testid="chat-conversation-row" className="w-full text-left py-3 flex items-start justify-between gap-3 hover:bg-white/5 rounded-xl px-2 -mx-2 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{c.primer_mensaje}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{fmt(c.last)} · {c.mensajes} mensajes{c.articulo && <span className="text-sky-300"> · desde /blog/{c.articulo}</span>}{c.lead_email && <span className="text-emerald-300"> · lead: {c.lead_email}</span>}</p>
                </div>
                <span className="text-xs text-blue-300 shrink-0">Ver</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <ChatConversationDialog sessionId={selected} onClose={() => setSelected(null)} onDelete={remove} />
    </div>
  );
};

export default ChatManager;
